import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ControlProps, optionIs, rankWith } from "@jsonforms/core";
import { withJsonFormsControlProps } from "@jsonforms/react";
import { ChevronsUpDown, PlaySquare, X } from "lucide-react";
import React, { useState } from "react";

import { useZoomGetRecording } from "@/components/EntitySelector/integrations/ZoomSelector/useZoomGetRecording";
import { useZoomListRecordings } from "@/components/EntitySelector/integrations/ZoomSelector/useZoomListRecordings";
import { useIntegrationConnection } from "@/hooks/useIntegrationConnection";
import { ZoomRecordingEntity } from "@cohesive/api-entities";
import { getEntityId, Spinner } from "@cohesive/ui-components";

interface ZoomRecordingControlProps extends ControlProps {
  data: ZoomRecordingEntity | string | undefined;
}

const ZoomRecordingControlComponent: React.FC<ZoomRecordingControlProps> = ({
  data,
  enabled,
  handleChange,
  path,
  label,
  visible,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>();

  // Extract ID from data (handles both string ID and entity object)
  // Handle both string IDs and entity objects
  const selectedId =
    typeof data === "string" ? data : data ? getEntityId(data as any) : null;

  const connection = useIntegrationConnection("zoom");

  const {
    data: recordingsData,
    isLoading: isZoomListRecordingsLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useZoomListRecordings({
    connection: connection!,
    dateRange,
  });

  // Flatten pages into single array
  const allRecordings =
    recordingsData?.pages?.flatMap((page) => page.data) ?? [];

  // Filter recordings based on search
  const filteredRecordings = allRecordings.filter((recording: any) =>
    recording.data.topic.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const isSingleEnabled =
    !!connection &&
    !!selectedId &&
    !allRecordings.find((r) => r.data.uuid === selectedId);

  // Fetch selected recording if not in list (for when navigating back to this step)
  const {
    data: selectedRecordingData,
    isLoading: isZoomListRecordingsSingleLoading,
  } = useZoomGetRecording({
    connection: connection ?? null,
    meetingId: selectedId ?? null,
    enabled: isSingleEnabled,
  });

  const loading = isSingleEnabled
    ? isZoomListRecordingsSingleLoading || isZoomListRecordingsLoading
    : isZoomListRecordingsLoading;

  // Handle loading earlier recordings
  const handleLoadEarlier = () => {
    const currentFrom = dateRange?.from
      ? new Date(dateRange.from)
      : new Date(new Date().setDate(new Date().getDate() - 30));

    const newFrom = new Date(currentFrom);
    newFrom.setMonth(newFrom.getMonth() - 3);

    const newTo = new Date(currentFrom);
    newTo.setDate(newTo.getDate() - 1);

    setDateRange({
      from: newFrom.toISOString().split("T")[0],
      to: newTo.toISOString().split("T")[0],
    });
  };

  // Find the selected recording from the list or use individually fetched one
  const selectedRecording =
    allRecordings.find((r: any) => r.data.uuid === selectedId) ||
    selectedRecordingData;

  if (!visible) return null;

  return (
    <div className="w-full">
      {label && <label className="text-sm font-medium">{label}</label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            disabled={!enabled || !connection}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            {selectedRecording ? (
              <div className="flex items-center gap-2 overflow-hidden">
                <div className="flex items-center justify-center w-7 h-7 shrink-0 rounded-full bg-muted">
                  <PlaySquare className="w-4! h-4!" />
                </div>
                <span className="truncate">
                  {selectedRecording.data.topic || "Untitled Recording"}
                </span>
              </div>
            ) : (
              <span className="text-muted-foreground">Select recording...</span>
            )}
            <div className="flex items-center gap-1 ml-2 shrink-0">
              {selectedRecording && (
                <div
                  role="button"
                  aria-label="Clear selection"
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleChange(path, null);
                  }}
                >
                  <X className="h-4 w-4 opacity-50 hover:opacity-100" />
                </div>
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search recordings..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList
              onScroll={(e: React.UIEvent<HTMLDivElement>) => {
                const element = e.currentTarget;
                const bottom =
                  element.scrollHeight - element.scrollTop ===
                  element.clientHeight;

                if (bottom && hasNextPage && !isFetchingNextPage) {
                  fetchNextPage();
                }
              }}
            >
              {loading ? (
                <div className="flex items-center justify-center p-4">
                  <Spinner size="sm" />
                </div>
              ) : (
                <>
                  <CommandEmpty>
                    {!dateRange ? (
                      <div className="py-6 text-center">
                        <p className="text-sm text-neutral-500 mb-3">
                          No recordings found in the last 30 days
                        </p>
                        <button
                          onClick={handleLoadEarlier}
                          className="text-sm text-teal hover:text-teal font-medium"
                        >
                          Search Earlier Recordings
                        </button>
                      </div>
                    ) : (
                      <div className="py-6 text-center">
                        <p className="text-sm text-neutral-500 mb-3">
                          No recordings found in this date range
                        </p>
                        <button
                          onClick={handleLoadEarlier}
                          className="text-sm text-teal hover:text-teal font-medium"
                        >
                          Search Earlier Recordings
                        </button>
                      </div>
                    )}
                  </CommandEmpty>
                  <CommandGroup>
                    {filteredRecordings.map((recording: any) => (
                      <CommandItem
                        key={recording.data.uuid}
                        value={recording.data.uuid}
                        onSelect={() => {
                          // Return full entity object
                          handleChange(path, recording);
                          setOpen(false);
                        }}
                      >
                        <div className="flex items-center justify-center w-7 h-7 shrink-0 rounded-full bg-muted">
                          <PlaySquare className="w-4! h-4!" />
                        </div>
                        <span className="truncate">
                          {recording.data.topic || "Untitled Recording"}
                        </span>
                      </CommandItem>
                    ))}
                    {isFetchingNextPage && (
                      <div className="py-2 text-center">
                        <Spinner size="sm" />
                      </div>
                    )}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export const zoomRecordingTester = rankWith(
  5,
  optionIs("control", "zoom_recording"),
);

export const ZoomRecordingControl = withJsonFormsControlProps(
  ZoomRecordingControlComponent,
);
